import React, { useCallback, useRef, useState } from "react";
import DataGrid, {
  Column,
  Pager,
  Paging,
  Scrolling,
  SearchPanel,
  Selection,
  Summary,
  TotalItem,
} from "devextreme-react/cjs/data-grid";
import CustomStore from "devextreme/data/custom_store";
import { Card } from "react-bootstrap";
import { HttpStatus, UpdateInwardDataIds } from "modules/core";
import { updateGoldAndDiamondInwardRecords } from "utils/inward";
import notistack from "modules/core/notistack";
import { getAllSNMCCDiamondInwardData } from "../services/diamond-product-inward-snmcc.service";
import useMenuData from "Layout/LayoutMenuData";
import { HasPermission } from "Layout/Permissions/permissionsCache";

const DiamondProductInwardSNMCC = () => {
  const { loadStats } = useMenuData()

  let setSelectedRows: Array<number> = [];
  const dataGridRef = useRef<DataGrid>(null);

  const dataSource = new CustomStore({
    key: 'id',
    load: async (loadOptions) => {
      try {
        const { searchValue, skip, take } = loadOptions;
        const parameterList = new URLSearchParams();
        if (typeof skip === 'number') parameterList.append('skip', skip.toString());
        if (typeof take === 'number') parameterList.append('take', take.toString());
        const search = JSON.parse(JSON.stringify(loadOptions));
        for (const [key, value] of Object.entries(search)) {
          parameterList.append(key, JSON.stringify(value));
        }
        if (searchValue) {
          parameterList.append("search", searchValue);
        }
        parameterList.append("MaterialType", "diamond");
        parameterList.append("IsSNMCC", "true");
        parameterList.append("IsReceived", "false");
        const response = await getAllSNMCCDiamondInwardData(parameterList);
        return response;
      } catch (error) {
        console.error("Data loading error:", error);
        return { data: [], totalCount: 0 };
      }
    }
  });

  const handleSelectionChange = (e: any) => {
    setSelectedRows = e.selectedRowsData.map((item: any) => item.id);
  };

  const renderSerialNumber = useCallback((cellData: any) => {
    const rowIndex = cellData.rowIndex + 1 + cellData.component.pageIndex() * cellData.component.pageSize();
    return <span>{rowIndex}</span>;
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateDiamondAndGoldInword = async () => {
    try {
      setIsSubmitting(true);
      await loadStats();
      if (setSelectedRows.length > 0) {
        const model = new UpdateInwardDataIds();
        model.productHeaderIdList = setSelectedRows;
        const response = await updateGoldAndDiamondInwardRecords(model);
        if (response.status === HttpStatus.Success) {
          dataGridRef.current?.instance.clearSelection();
          dataGridRef.current?.instance.refresh();
          notistack.notifyFromApi(response);
        } else {
          notistack.notifyFromApi(response);
        }
      } else {
        notistack.warning("Please Select Product");
      }
    } catch (error) {
      console.error("Failed to fetch inward Data:", error);
    } finally {
      await loadStats();
      setIsSubmitting(false);
    }
  };


  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="m-0"> Memo Diamond Inward</h5>
              {HasPermission('ProductHeader.Write') && <button onClick={updateDiamondAndGoldInword} className="submit-btn" disabled={isSubmitting}>
                Receive
              </button>
              }
            </Card.Header>
            <Card.Body className="pt-0">
              {HasPermission('ProductHeader.Read') &&
                <DataGrid
                  dataSource={dataSource}
                  remoteOperations={{ groupPaging: true }}
                  showRowLines={true}
                  keyExpr="id"
                  ref={dataGridRef}
                  showBorders={true}
                  columnAutoWidth={true}
                  onSelectionChanged={handleSelectionChange}
                  onContentReady={(e) => {
                    const hasData = e.component.totalCount() > 0;
                    e.component.option("pager.visible", hasData);
                    e.component.option("paging.visible", hasData);
                  }}
                  onCellPrepared={(e) => {
                    if (['data', 'header'].includes(e.rowType)) {
                      e.cellElement.style.textAlign = 'center';
                    }
                  }}
                >
                  <Scrolling
                    mode='standard'
                    useNative={true}
                    scrollByContent={true}
                    scrollByThumb={true}
                  />
                  <Selection mode="multiple" />
                  <Pager

                    showPageSizeSelector={true}
                    allowedPageSizes={[10, 25, 50, 100]}
                    showNavigationButtons={true}
                    displayMode="full"
                    showInfo={true}
                  />
                  <Summary>
                    <TotalItem
                    // column="Sr.No."
                    // summaryType="count"
                    // displayFormat="Total: {0}"
                    />
                  </Summary>
                  <Paging defaultPageSize={10} />
                  <SearchPanel visible width={240} placeholder="Search..." />

                  <Column
                    cssClass="text-center" caption="Sr.No."
                    dataType="number"
                    alignment="center"
                    cellRender={renderSerialNumber}
                  />

                  <Column
                    allowFiltering={true}
                    dataField="storeCode"
                    caption="Store Code"
                    dataType="string"
                    width={150}
                  />

                  <Column
                    allowFiltering={true}
                    dataField="styleNo"
                    caption="Style Number"
                    dataType="string"
                    width={150}
                  />

                  <Column
                    allowFiltering={true}
                    dataField="categoryName"
                    caption="Category Name"
                    dataType="string"
                    width={150}
                  />

                  <Column
                    allowFiltering={true}
                    dataField="certificateNo"
                    caption="IGI No."
                    dataType="string"
                    width={150}
                  />
                  <Column
                    allowFiltering={true}
                    dataField="huid"
                    cssClass="text-center"
                    caption="HUID"
                    dataType="string"
                    width={150}
                  />
                  <Column
                    allowFiltering={false}
                    dataField="grossWt"
                    caption="Gross Weight"
                    format="#,##0.000"
                    dataType="number"
                    width={150}
                  />
                  <Column
                    allowFiltering={false}
                    dataField="metalKT"
                    caption="Metal KT"
                    dataType="string"
                    width={150}
                  />
                  <Column
                    allowFiltering={false}
                    dataField="diaQty"
                    caption="Diamond Quality"
                    dataType="string"
                    width={150}
                  />
                  <Column
                    allowFiltering={true}
                    dataField="bagNo"
                    caption="BagNo."
                    dataType="string"
                    width={150}
                  />
                  <Column
                    dataField="isSRP" caption="Price Type" dataType="boolean" cellRender={({ data }) => (
                      <span>{data?.isSRP ? "SRP" : "MRP"}</span>
                    )} width={150}
                  />


                  <Column
                    allowFiltering={false}
                    dataField="dpPrice"
                    caption="DP"
                    format="#,##0.00"
                    dataType="string"
                    width={150}
                  />


                </DataGrid>
              }
            </Card.Body>
          </Card>
        </div>
      </div>
    </React.Fragment>
  );
};
export default DiamondProductInwardSNMCC;
